import { useEffect, useState } from "react";
import { nodesApi } from "@api/enpoints/nodes-api";
import userService from "@utility/user-service";
/**
 * "Is this user a Super Admin?" — decides whether the Free Roaming Nodes item appears in
 * the sidebar (Super Admin only).
 *
 * Both the desktop sidebar and the mobile navigation ask for this, so the in-flight request
 * is cached at module level and every consumer shares the single call. The cache is keyed by
 * user id: logout/login is a client-side route change (no page reload), so this module
 * survives the auth boundary, and without the key a new user would inherit the previous
 * user's cached answer. Call `invalidateFreeRoamingSummary()` after an admin flips the switch
 * on a node so the next read refetches.
 */
const EMPTY = {
    hasAny: false,
    count: 0,
    isSuperAdmin: false,
};
let cached = null;
let cachedForUserId = null;
const subscribers = new Set();
const currentUserId = () => { var _a, _b; return (_b = (_a = userService.getUser()) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null; };
const load = () => {
    const userId = currentUserId();
    if (cached && cachedForUserId !== userId) {
        cached = null;
    }
    if (!cached) {
        cachedForUserId = userId;
        cached = nodesApi
            .getFreeRoamingSummary()
            .then((response) => { var _a, _b; return (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : EMPTY; })
            // A failure here must never break the sidebar — fall back to "no free roaming nodes".
            .catch(() => EMPTY);
    }
    return cached;
};
/** Drops the cached answer and re-notifies every mounted consumer. */
export const invalidateFreeRoamingSummary = () => {
    cached = null;
    load().then((value) => subscribers.forEach((notify) => notify(value)));
};
export const useFreeRoamingNodesSummary = () => {
    const [summary, setSummary] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let mounted = true;
        const apply = (value) => {
            if (!mounted)
                return;
            setSummary(value);
            setLoading(false);
        };
        subscribers.add(apply);
        load().then(apply);
        return () => {
            mounted = false;
            subscribers.delete(apply);
        };
    }, []);
    return Object.assign(Object.assign({}, summary), { loading });
};
